import {useCallback, useEffect, useMemo, useRef, type KeyboardEvent} from "react";
import {DefaultVatRateOption} from "../types/VatCode.ts";
import {LineItemsRow, type LineCell} from './LineItemsRow.tsx';
import {BookingProvider, isDefaultBookingProvider} from '../../../types/enums.ts';
import type { GlAccount } from "../../../hooks/useGLAccounts.ts";
import type { FormInstance } from "../../../components/form-library/types.ts";
import {decimalToLocalizedString, Money} from "../../../lib/Money.ts";
import {cn, getCurrencySymbol} from "../../../lib/utils.ts";
import type { LineItemNeedingAttention } from "../types/LineItem.ts";
import type {CurrencyEnum, DocumentDirection} from '../../../generated/enums.ts';
import type {MonetaryOrigin} from '../../../components/general/ForeignAmountLabel.tsx';

type LineItemsTableProps = {
      lineItemIds: string[]
      billedLineAmounts?: Record<string, {net?: Money | null; vat?: Money | null}>
      monetaryOrigin?: MonetaryOrigin | null
      lineItemsNeedingAttention?: LineItemNeedingAttention[]
      netAmountsSum: number
      vatAmountsSum: number
      form: FormInstance
      glAccounts: GlAccount[]
      documentDirection: DocumentDirection
      bookingProvider: BookingProvider
      defaultVatRateOptions: DefaultVatRateOption[]
      currency?: CurrencyEnum
      shouldShowBookingError: boolean
      invalid?: boolean
      disabled?: boolean
      onAddRow: () => string
      onDeleteRow: (itemId: string) => void
      onVatRateChange: (itemId: string, vatRatePercentage: number | null) => void
      onNetAmountChange: (itemId: string, netAmount: Money | null) => void
  }

type PendingFocus = { lineId: string; cell: LineCell };

const HEADER = 'whitespace-nowrap px-2.5 py-2 text-left text-[10.5px] font-semibold uppercase tracking-[0.045em] text-gray-500';

export function isDeleteLineShortcut(event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey'>): boolean {
    return (event.key === 'Backspace' || event.key === 'Delete') && (event.metaKey || event.ctrlKey);
}

function focusCell(root: HTMLElement | null, lineId: string, cell: LineCell) {
    root?.querySelector<HTMLElement>(`tr[data-line-id="${CSS.escape(lineId)}"] [data-cell="${cell}"] input`)?.focus();
}

function isEditingText(target: HTMLElement): boolean {
    return (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) && target.value !== '';
}

export function LineItemsTable({
    lineItemIds,
    billedLineAmounts,
    monetaryOrigin,
    lineItemsNeedingAttention = [],
    netAmountsSum,
    vatAmountsSum,
    form,
    glAccounts,
    documentDirection,
    defaultVatRateOptions,
    bookingProvider,
    currency = 'EUR',
    shouldShowBookingError,
    invalid = false,
    disabled = false,
    onAddRow,
    onDeleteRow,
    onVatRateChange,
    onNetAmountChange,
}: LineItemsTableProps) {
    const tableRef = useRef<HTMLTableElement>(null);
    const pendingFocusRef = useRef<PendingFocus | null>(null);

    const lineItemsNeedingAttentionById = useMemo(() => {
        const mapped: Record<string, LineItemNeedingAttention> = {};
        for (const item of lineItemsNeedingAttention) {
            mapped[item.lineItemId] = item;
        }
        return mapped;
    }, [lineItemsNeedingAttention]);

    useEffect(() => {
        const pending = pendingFocusRef.current;
        if (!pending) return;
        pendingFocusRef.current = null;
        focusCell(tableRef.current, pending.lineId, pending.cell);
    }, [lineItemIds]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTableSectionElement>) => {
            const target = event.target as HTMLElement;
            const row = target.closest<HTMLTableRowElement>('tr[data-line-id]');
            if (!row || disabled) return;
            const lineId = row.dataset.lineId as string;
            const index = lineItemIds.indexOf(lineId);
            if (index === -1) return;

            if (isDeleteLineShortcut(event)) {
                if (isEditingText(target)) return;
                event.preventDefault();
                const neighbour = lineItemIds[index + 1] ?? lineItemIds[index - 1];
                if (neighbour) pendingFocusRef.current = { lineId: neighbour, cell: 'description' };
                onDeleteRow(lineId);
                return;
            }

            const cell = target.closest<HTMLElement>('[data-cell]')?.dataset.cell as LineCell | undefined;
            if (event.key !== 'Enter' || !cell || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
            event.preventDefault();
            if (index === lineItemIds.length - 1) {
                pendingFocusRef.current = { lineId: onAddRow(), cell: 'description' };
                return;
            }
            focusCell(tableRef.current, lineItemIds[index + 1], cell);
        },
        [lineItemIds, disabled, onAddRow, onDeleteRow],
    );

    const hasDefaultBooking = isDefaultBookingProvider(bookingProvider);
    const canBook = hasDefaultBooking;
    const columnCount = canBook ? 7 : 6;
    const currencySymbol = getCurrencySymbol(currency);
    return (
        <div
            className={cn(
                'scrollbar-subtle overflow-x-auto rounded-lg border bg-white shadow-sm',
                invalid ? 'border-red-300 ring-1 ring-red-100' : 'border-gray-200',
            )}
        >
            <table
                ref={tableRef}
                className={cn('w-full border-collapse text-[13px]', canBook ? 'min-w-[780px]' : 'min-w-[610px]')}
            >
                <thead className="border-b border-gray-200 bg-gray-50/90">
                    <tr>
                        <th scope="col" className={cn(HEADER, 'w-10 text-center')}>
                            <span className="sr-only">Regelnummer</span>
                            #
                        </th>
                        {canBook && <th scope="col" className={cn(HEADER, 'w-[11rem]')}>Grootboekrekening</th>}
                        <th scope="col" className={cn(HEADER, 'min-w-[8rem]')}>Omschrijving</th>
                        <th scope="col" className={cn(HEADER, 'w-[9rem] text-right')}>Bedrag excl.</th>
                        <th scope="col" className={cn(HEADER, 'w-[6.5rem]')}>BTW</th>
                        <th scope="col" className={cn(HEADER, 'w-[7rem] text-right')}>BTW-bedrag</th>
                        <th scope="col" className={cn(HEADER, 'w-[4.75rem]')}>
                            <span className="sr-only">Acties</span>
                        </th>
                    </tr>
                </thead>
                <tbody onKeyDown={handleKeyDown} className="divide-y divide-gray-100">
                    {lineItemIds.length === 0 ? (
                        <tr>
                            <td colSpan={columnCount} className="px-4 py-10 text-center">
                                <p className="text-[13px] font-medium text-gray-700">Nog geen boekingsregels</p>
                                <p className="mt-1 text-[12px] text-gray-500">
                                    Voeg een regel toe om bedragen en grootboekrekeningen vast te leggen.
                                </p>
                            </td>
                        </tr>
                    ) : (
                        lineItemIds.map((itemId, index) => {
                            const attentionInfo = lineItemsNeedingAttentionById[itemId];
                            return (
                            <LineItemsRow
                                key={itemId}
                                itemId={itemId}
                                index={index}
                                billedNetAmount={billedLineAmounts?.[itemId]?.net}
                                billedVatAmount={billedLineAmounts?.[itemId]?.vat}
                                monetaryOrigin={monetaryOrigin}
                                shouldShowBookingError={shouldShowBookingError}
                                form={form}
                                glAccounts={glAccounts}
                                documentDirection={documentDirection}
                                attentionData={{
                                    requiresAttention: Boolean(attentionInfo),
                                    attentionMessage: attentionInfo?.message ?? null,
                                }}
                                defaultVatRateOptions={defaultVatRateOptions}
                                bookingProvider={bookingProvider}
                                currency={currency}
                                onDelete={() => onDeleteRow(itemId)}
                                onVatRateChange={onVatRateChange}
                                onNetAmountChange={onNetAmountChange}
                            />
                            );
                        })
                    )}
                </tbody>
                {lineItemIds.length > 0 && (
                    <tfoot className="border-t border-gray-200 bg-gray-50/90 text-[12px] font-semibold">
                        <tr>
                            <td colSpan={canBook ? 3 : 2} className="px-2.5 py-2.5 text-right font-medium text-gray-500">
                                Som van de regels
                            </td>
                            <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums text-gray-950">
                                {currencySymbol} {decimalToLocalizedString(netAmountsSum / 100)}
                            </td>
                            <td className="px-2.5 py-2.5 text-right font-medium text-gray-500">BTW</td>
                            <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums text-gray-950">
                                {currencySymbol} {decimalToLocalizedString(vatAmountsSum / 100)}
                            </td>
                            <td />
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
